import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { usePasteListener } from '@databases/copy-paste/usePasteListener';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectFieldIdsByContentId,
  selectIsValueSelected,
  selectSelectedValuesCount,
} from '@databases/redux/cellSelections/selectors';
import { DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl-next';

import { useAttachmentPaste } from '../hooks/use-attachment-paste';
import { useAttachmentUpload } from '../hooks/use-attachment-upload';
import { useMediaCollectionId } from '../hooks/use-media-collection-id';

import AttachmentValueGridView from './AttachmentValueGridView';
import { type EditComponentProps } from './types';

const attachmentWrapperStyles = xcss({
  // Ensures no hover clashing between attachment field and row drag handler
  marginLeft: 'space.200',
});

const getBoxStyles = (isDragActive: boolean) => {
  return xcss({
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'center',

    width: `calc(100% - ${token('space.075', '0.375rem')})`,
    border: `${token('border.width.selected', '2px')} dashed ${
      isDragActive ? token('color.background.neutral', '#091E420F') : 'transparent'
    }`,
    borderRadius: 'radius.small',
    height: `calc(100% - ${token('space.075', '0.375rem')})`,
    backgroundColor: isDragActive
      ? 'color.background.accent.blue.subtlest.hovered'
      : undefined,
    transition: `border 0.2s`,
  });
};

type Value = RawDatabaseValue<DatabaseFieldType.ATTACHMENT>[0];

const AttachmentValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.ATTACHMENT>
> = ({ contentId, onChange, onBlur, field, entryId, value, isSeamless = false }) => {
  const { mediaCollectionId } = useMediaCollectionId(contentId);
  const store = defaultStoreUtils.useStore();
  const [uploadOrder, setUploadOrder] = useState<number[]>([]); // Represents indices for value that are uploading
  const { formatMessage } = useIntl();
  const length = value.length;
  const isFirstField =
    selectFieldIdsByContentId(store.getState(), {
      contentId,
    })[0] === field.id;

  const valueIsTheOnlyValueSelected = defaultStoreUtils.useEqualSelector((state) => {
    if (selectSelectedValuesCount(state, { contentId }) === 1 && entryId) {
      return selectIsValueSelected(state, { fieldId: field.id, entryId });
    }
    return false;
  });

  const isUploading = useMemo(() => {
    return uploadOrder.length > 0;
  }, [uploadOrder]);

  const onItemChange = useCallback(
    (value: Value, index: number, _: null, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.EDIT,
          value,
          index,
        },
      });
    },
    [onChange]
  );

  const onItemAdd = useCallback(
    (value: Value, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: value,
        },
      });
    },
    [onChange]
  );

  const onItemDelete = useCallback(
    (_: Value, index: number, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index,
        },
      });
    },
    [onChange]
  );

  const handleChangeFile = useCallback(
    (file: Value, index: number) => {
      onItemChange(file, index, null, value);
    },
    [onItemChange, value]
  );

  const handleAddFile = useCallback(
    (file: Value) => {
      onItemAdd(file, value);
    },
    [onItemAdd, value]
  );

  // Delete values without fileId and not currently uploading (to avoid placeholders showing when unnecessary)
  useEffect(() => {
    value.forEach((val, index) => {
      if (!uploadOrder.length && 'fileId' in val && !val.fileId) {
        onItemDelete(value[index], index, value);
      }
    });
  }, [value, uploadOrder, onItemDelete]);

  const { handleDroppedFiles } = useAttachmentUpload({
    contentId,
    arrayValue: value,
    mediaCollectionId,
    setUploadOrder,
    handleAddFile,
    handleChangeFile,
  });
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onFileDialogCancel: () => {
      onBlur?.();
    },
    noClick: true, // Opening the dialog is handled by the "Add files" button
    disabled: uploadOrder.length > 0,
    noDrag: false,
    onDropAccepted: useCallback(
      (acceptedFiles: File[]) => {
        handleDroppedFiles(acceptedFiles);
      },
      [handleDroppedFiles]
    ),
  });

  const { handlePaste: onPaste } = useAttachmentPaste({
    valueIsTheOnlyValueSelected,
    handleDroppedFiles,
  });

  usePasteListener({
    onPaste,
  });

  const attachmentValueEdit = useMemo(() => {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: token('space.025', '0.125rem'),
        }}
      >
        <Box {...getRootProps()} xcss={getBoxStyles(isDragActive)}>
          {/** Use dropzone hook for dragging files into entire value cell (only adding) */}
          <div
            style={{
              maxWidth: '100%',
              width: '100%',
              height: `calc(100% - ${token('space.050', '0.25rem')}px)`,
            }}
          >
            <input {...getInputProps()} />

            {valueIsTheOnlyValueSelected && length === 0 ? (
              <Inline
                xcss={xcss({
                  width: '100%',
                  height: '100%',
                })}
              >
                {!isUploading && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                    appearance="subtle"
                    iconBefore={<AddIcon label={''} />}
                  >
                    <div
                      {...getRootProps({
                        style: {
                          cursor: !value.length ? 'pointer' : 'default',
                        },
                      })}
                    >
                      {formatMessage(messages['media.add-file'], {
                        isMulti: 2, // Multi is always enabled
                      })}
                    </div>
                  </Button>
                )}
              </Inline>
            ) : (
              <AttachmentValueGridView
                contentId={contentId}
                onChange={onChange}
                onBlur={onBlur}
                field={field}
                entryId={entryId}
                value={value}
                isSeamless={isSeamless}
              />
            )}
          </div>
        </Box>
      </Box>
    );
  }, [
    contentId,
    entryId,
    field,
    formatMessage,
    getInputProps,
    getRootProps,
    isDragActive,
    isSeamless,
    isUploading,
    length,
    onBlur,
    onChange,
    open,
    value,
    valueIsTheOnlyValueSelected,
  ]);

  return (
    <>
      {isFirstField ? (
        <Box xcss={attachmentWrapperStyles}>{attachmentValueEdit}</Box>
      ) : (
        attachmentValueEdit
      )}
    </>
  );
};

export default AttachmentValueEdit;
